/**
 * 실행계획 탭의 본문 — 좌측 계획 트리 + 우측 오브젝트 상세.
 *
 * `MainDisplayTabs` 안에 있던 것을 **그대로** 옮겼다(2026-09-20). 개선 전/후 비교 탭이 이것을
 * 좌우에 하나씩 그려야 하는데, `MainDisplayTabs` 안에 두면 순환 import 가 된다.
 */
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { FiCopy, FiDownload, FiBookmark, FiFileText, FiRefreshCw, FiCpu, FiActivity } from 'react-icons/fi';
import PlanOptionsMenu from './PlanOptionsMenu';
import { parseXplanText } from '../utils/xplanParser';
import { ownerByName, qualify } from '../utils/planObjects';
import PlanInsights from './assistant/PlanInsights';
import SplitView from './SplitView';
import styles from './MainDisplayTabs.module.css';
import { monoWide } from '../utils/monoWide';
import { minimapLineHeight, minimapViewport, minimapScrollTarget } from '../utils/minimapScale';
import { listenDrag } from '../utils/dragListen';   // 한글이 든 줄의 세로 정렬 — 넓은 글자를 정확히 2칸으로

const STATS_API = process.env.REACT_APP_API_BASE_URL || '/sql-developer';

// 실행계획 인터랙티브 렌더링 — 좌측 실행계획 + 우측 오브젝트 상세
/** 받아 온 통계를 합친다 — 같은 것(소유자+이름)은 한 번만. 다시 받아도 카드가 두 번 그려지지 않게 */
function mergeStats(prev, add) {
    const tKey = (x) => `${x.OWNER}.${x.TABLE_NAME}`;
    const iKey = (x) => `${x.TABLE_OWNER}.${x.TABLE_NAME}.${x.INDEX_NAME}`;
    const tables = [...prev.tables]; const seenT = new Set(tables.map(tKey));
    for (const x of (add && add.tables) || []) if (!seenT.has(tKey(x))) { seenT.add(tKey(x)); tables.push(x); }
    const indexes = [...prev.indexes]; const seenI = new Set(indexes.map(iKey));
    for (const x of (add && add.indexes) || []) if (!seenI.has(iKey(x))) { seenI.add(iKey(x)); indexes.push(x); }
    return { tables, indexes };
}

const InteractivePlanContent = ({ activeTab, onPlanNodeClick, activePlanNodeId, isResultSplit = false, statsConnectionId, onShowTableDDL, showPlanObjectDetail = false,
                                 onSavePlanToArchive, onExportPlanFile, onRequestObjectDetail, onPlanToReview, onPlanMonitorReport,
                                 planOptions, onPlanOptionsChange, onRerunPlan, onAiTunePlan }) => {
    const { t } = useTranslation();
    const parsed = useMemo(() => {
        if (!activeTab?.data) return null;
        return parseXplanText(activeTab.data);
    }, [activeTab?.data]);

    const [objectStats, setObjectStats] = useState({ tables: [], indexes: [] });
    const [highlightObject, setHighlightObject] = useState(null);
    const [selectedObject, setSelectedObject] = useState(null); // 클릭한 오브젝트명 (필터링용)
    // 조회 결과에서 뽑은 리본 필터. **빈 집합 = 미적용(전부 보임)** 이다 —
    // "아무것도 안 골랐으면 아무것도 안 보인다" 로 만들면 처음 열었을 때 빈 화면이 된다.
    // 인덱스 상세 통계는 **기본으로 감춘다.** 평소 보는 것은 어떤 컬럼을 어떤 순서로 묶었나와
    // 언제 만들었나이고, 나머지 수치는 튜닝할 때만 필요하다.
    const [showIdxStats, setShowIdxStats] = useState(false);
    const [typeFilter, setTypeFilter] = useState(() => new Set());
    const [schemaFilter, setSchemaFilter] = useState(() => new Set());
    // 인덱스 목록은 **접어 두고 개수만** 보인다 — 누르면 그 테이블 것만 펼친다(키: OWNER.TABLE).
    // 찾은 것이 인덱스면(계획의 인덱스 줄을 눌렀거나 인덱스 이름으로 검색) 그 카드는 처음부터 펼친다
    const [openIdx, setOpenIdx] = useState(() => new Set());
    // 계획이 읽는 객체의 소유자(서버가 계획 표에서 읽어 준다) — 이름이 한 소유자로 정해질 때만 그 스키마로 좁힌다
    const owners = useMemo(() => ownerByName(activeTab?.planObjects), [activeTab?.planObjects]);
    const [lookupBusy, setLookupBusy] = useState(false);
    const [bookmarkedLines, setBookmarkedLines] = useState(new Set());
    const [planSearch, setPlanSearch] = useState({ text: '', matchIndex: -1, matchCount: 0, matchLines: [] });
    // 복사했다는 표시는 잠깐만 — 토스트를 띄우면 계획을 읽는 화면을 가린다
    const [copied, setCopied] = useState(false);
    const hasPlanText = !!(activeTab && typeof activeTab.data === 'string' && activeTab.data.trim());
    const rightPanelRef = useRef(null);
    const planScrollRef = useRef(null);
    const planMinimapRef = useRef(null);

    // 실행계획의 오브젝트 목록 추출 → 통계 자동 로드
    useEffect(() => {
        if (!parsed || parsed.nodes.length === 0) return;
        const objNames = [...new Set(
            parsed.nodes.filter(n => n.objectName).map(n => n.objectName.toUpperCase())
        )];
        if (objNames.length === 0) return;

        // 통계는 **이 계획을 만든 연결**에서 봐야 한다. 안 넘기면 서버가 400 을 주고
        // 우측 패널이 조용히 빈다 — 실제로 그렇게 묻혀 있었다.
        if (!statsConnectionId) { setObjectStats({ tables: [], indexes: [] }); return; }
        // 소유자를 아는 이름은 `OWNER.NAME` 으로 — 서버가 그 스키마의 것만 준다(모르면 예전 규칙: 현재 스키마 → 없으면 전부)
        axios.post(`${STATS_API}/get-object-stats`, { objectNames: objNames.map(n => qualify(n, owners)), connectionId: statsConnectionId })
            .then(res => setObjectStats(res.data))
            .catch((err) => {
                // 조용히 비우면 왜 안 나오는지 알 수가 없다
                console.warn('[plan] 오브젝트 통계 조회 실패:', (err.response && err.response.data && err.response.data.error) || err.message);
                setObjectStats({ tables: [], indexes: [] });
            });
    }, [parsed, statsConnectionId, owners]);

    /**
     * 검색어에 걸린 테이블과, 거기서 뽑은 리본 재료.
     *
     * 리본에 적히는 개수와 실제로 그려지는 목록이 **같은 계산**에서 나와야 한다. 두 벌로 두면
     * "테이블 3" 이라 적어 놓고 두 개만 그리는 일이 생긴다.
     */
    const matched = useMemo(() => {
        if (!selectedObject) return { tables: [], schemas: [], indexCount: 0 };
        const q = selectedObject.toUpperCase();
        const tables = objectStats.tables.filter(tb => {
            if (tb.TABLE_NAME.includes(q)) return true;
            // 인덱스명으로도 찾는다 — 인덱스 이름만 아는 경우가 흔하다
            return objectStats.indexes.some(idx => idx.INDEX_NAME.includes(q) && idx.TABLE_NAME === tb.TABLE_NAME && idx.TABLE_OWNER === tb.OWNER);
        });
        const byOwner = new Map();
        for (const tb of tables) byOwner.set(tb.OWNER, (byOwner.get(tb.OWNER) || 0) + 1);
        const indexCount = tables.reduce(
            (n, tb) => n + objectStats.indexes.filter(idx => idx.TABLE_NAME === tb.TABLE_NAME && idx.TABLE_OWNER === tb.OWNER).length, 0);
        return {
            tables,
            schemas: [...byOwner.entries()].map(([owner, n]) => ({ owner, n })).sort((a, b) => a.owner.localeCompare(b.owner)),
            indexCount,
        };
    }, [objectStats, selectedObject]);

    // 리본 선택 반영. **빈 집합이면 그 축은 안 거른다**(미적용).
    const showTables = typeFilter.size === 0 || typeFilter.has('table');
    const showIndexes = typeFilter.size === 0 || typeFilter.has('index');
    const visibleTables = useMemo(
        () => matched.tables.filter(tb => schemaFilter.size === 0 || schemaFilter.has(tb.OWNER)),
        [matched.tables, schemaFilter]);

    const toggleIn = (setFn) => (key) => setFn(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });
    const toggleType = toggleIn(setTypeFilter);
    const toggleSchema = toggleIn(setSchemaFilter);

    // 검색어가 바뀌면 리본 선택을 푼다 — 앞 결과의 스키마가 새 결과에 없으면 빈 목록만 남는다
    useEffect(() => {
        setTypeFilter(new Set());
        setSchemaFilter(new Set());
        setOpenIdx(new Set());
    }, [selectedObject]);

    // 우측 패널에서 해당 오브젝트로 스크롤 (없으면 추가 로드)
    const scrollToObject = useCallback((name) => {
        setHighlightObject(name);
        setSelectedObject(name);

        // 해당 테이블이 통계에 없으면 추가 로드. **인덱스 이름도 본다** — 인덱스 줄을 누를 때마다 다시 받아 오던 것을 막는다
        const hasTable = objectStats.tables.some(t => t.TABLE_NAME === name) || objectStats.indexes.some(ix => ix.INDEX_NAME === name);
        if (!hasTable) {
            axios.post(`${STATS_API}/get-object-stats`, { objectNames: [qualify(name, owners)], connectionId: statsConnectionId })
                .then(res => {
                    if (res.data?.tables?.length > 0 || res.data?.indexes?.length > 0) {
                        setObjectStats(prev => mergeStats(prev, res.data));
                    }
                    // 로드 후 스크롤
                    setTimeout(() => {
                        if (rightPanelRef.current) {
                            const el = rightPanelRef.current.querySelector(`[data-obj="${name}"]`);
                            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                    }, 300);
                })
                .catch(() => {});
        } else {
            if (rightPanelRef.current) {
                const el = rightPanelRef.current.querySelector(`[data-obj="${name}"]`);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
        setTimeout(() => setHighlightObject(null), 3000);
    }, [objectStats, statsConnectionId, owners]);

    /**
     * 검색 칸에서 Enter — **서버에서 찾는다.** 타이핑은 이미 받아 둔 것(이 계획의 객체) 안에서만 거르므로, 계획에 없는 테이블·인덱스는
     * 여기로 찾는다. 이름은 정확히(테이블 이름이나 인덱스 이름 — 인덱스면 그 인덱스의 테이블이 나온다). `OWNER.NAME` 도 된다.
     */
    const lookupTyped = useCallback(() => {
        const q = String(selectedObject || '').trim().toUpperCase();
        if (!q || !statsConnectionId || lookupBusy) return;
        setLookupBusy(true);
        axios.post(`${STATS_API}/get-object-stats`, { objectNames: [q.includes('.') ? q : qualify(q, owners)], connectionId: statsConnectionId })
            .then((res) => {
                setObjectStats(prev => mergeStats(prev, res.data));
                // `OWNER.NAME` 으로 찾았으면 걸러 볼 글은 이름만 남긴다(목록은 이름으로 거른다)
                if (q.includes('.')) setSelectedObject(q.slice(q.lastIndexOf('.') + 1));
            })
            .catch(() => { /* 못 찾으면 아래 「없음」 안내가 그대로 보인다 */ })
            .finally(() => setLookupBusy(false));
    }, [selectedObject, statsConnectionId, owners, lookupBusy]);

    // 책갈피 토글
    /**
     * 계획 원문을 클립보드로. 지금까지 이 화면에는 **복사 버튼조차 없어서** 사람이 드래그로
     * 긁어야 했다(줄 번호까지 딸려 왔다).
     */
    const handleCopyPlan = useCallback(async () => {
        const text = (activeTab && activeTab.data) || '';
        if (!text) return;
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (_e) {
            // 클립보드가 막힌 환경(비보안 컨텍스트 등)에서도 화면이 죽지 않게
            setCopied(false);
        }
    }, [activeTab]);

    const togglePlanBookmark = useCallback((lineIdx) => {
        setBookmarkedLines(prev => {
            const next = new Set(prev);
            if (next.has(lineIdx)) next.delete(lineIdx); else next.add(lineIdx);
            return next;
        });
    }, []);

    // 실행계획 검색
    const handlePlanSearch = useCallback((text) => {
        if (!activeTab?.data || !text) {
            setPlanSearch({ text, matchIndex: -1, matchCount: 0, matchLines: [] });
            return;
        }
        const lines = activeTab.data.split('\n');
        const upper = text.toUpperCase();
        const matchLines = [];
        lines.forEach((line, idx) => { if (line.toUpperCase().includes(upper)) matchLines.push(idx); });
        setPlanSearch({ text, matchCount: matchLines.length, matchIndex: matchLines.length > 0 ? 0 : -1, matchLines });
        // 첫 매칭으로 스크롤
        if (matchLines.length > 0 && planScrollRef.current) {
            const el = planScrollRef.current.querySelector(`[data-line="${matchLines[0]}"]`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [activeTab?.data]);

    const handlePlanSearchNav = useCallback((direction) => {
        const { matchLines, matchIndex } = planSearch;
        if (matchLines.length === 0) return;
        const newIndex = direction === 'next'
            ? (matchIndex + 1) % matchLines.length
            : (matchIndex - 1 + matchLines.length) % matchLines.length;
        setPlanSearch(prev => ({ ...prev, matchIndex: newIndex }));
        if (planScrollRef.current) {
            const el = planScrollRef.current.querySelector(`[data-line="${matchLines[newIndex]}"]`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [planSearch]);

    // 미니맵 드로잉
    const drawPlanMinimap = useCallback(() => {
        const canvas = planMinimapRef.current;
        const scrollEl = planScrollRef.current;
        if (!canvas || !scrollEl || !activeTab?.data) return;

        const lines = activeTab.data.split('\n');
        const lineCount = lines.length;
        const dpr = window.devicePixelRatio || 1;
        const w = canvas.offsetWidth;
        const h = canvas.offsetHeight;
        canvas.width = w * dpr;
        canvas.height = h * dpr;
        const ctx = canvas.getContext('2d');
        ctx.scale(dpr, dpr);
        ctx.clearRect(0, 0, w, h);

        if (lineCount === 0) return;
        // 한 줄은 최대 3px — 에디터 미니맵과 같은 축척(utils/minimapScale)
        const lineH = minimapLineHeight(h, lineCount);

        // 코드 라인
        for (let i = 0; i < lineCount; i++) {
            const len = Math.min(lines[i].length, 150);
            if (len === 0) continue;
            const barW = Math.max(1, (len / 150) * w * 0.8);
            ctx.fillStyle = 'rgba(100, 120, 150, 0.3)';
            ctx.fillRect(0, i * lineH, barW, Math.max(1, lineH - 0.5));
        }

        // 책갈피 표시
        for (const bm of bookmarkedLines) {
            const y = bm * lineH;
            ctx.fillStyle = 'rgba(33, 150, 243, 0.6)';
            ctx.fillRect(0, y, 3, Math.max(2, lineH));
            ctx.fillStyle = 'rgba(33, 150, 243, 0.15)';
            ctx.fillRect(0, y, w, Math.max(2, lineH));
        }

        // 뷰포트
        const vp = minimapViewport({ height: h, lineCount, scrollTop: scrollEl.scrollTop, scrollHeight: scrollEl.scrollHeight, clientHeight: scrollEl.clientHeight });
        if (vp) {
            const vpTop = vp.top;
            const vpH = vp.height;
            ctx.fillStyle = 'rgba(0, 120, 215, 0.12)';
            ctx.fillRect(0, vpTop, w, vpH);
            ctx.strokeStyle = 'rgba(0, 120, 215, 0.3)';
            ctx.lineWidth = 1;
            ctx.strokeRect(0.5, vpTop + 0.5, w - 1, vpH - 1);
        }
    }, [activeTab?.data, bookmarkedLines]);

    // 미니맵 스크롤 연동
    useEffect(() => {
        const el = planScrollRef.current;
        if (!el) return;
        let rafId = null;
        const redraw = () => { if (rafId) cancelAnimationFrame(rafId); rafId = requestAnimationFrame(drawPlanMinimap); };
        el.addEventListener('scroll', redraw, { passive: true });
        redraw();
        const interval = setInterval(redraw, 2000);
        return () => { el.removeEventListener('scroll', redraw); clearInterval(interval); if (rafId) cancelAnimationFrame(rafId); };
    }, [drawPlanMinimap]);

    // 미니맵 클릭/드래그
    const handlePlanMinimapDrag = useCallback((e) => {
        e.preventDefault();
        const el = planScrollRef.current;
        const canvas = planMinimapRef.current;
        if (!el || !canvas) return;
        const lineCount = (activeTab?.data || '').split('\n').length;
        const doScroll = (ev) => {
            const rect = canvas.getBoundingClientRect();
            // 그리기와 같은 축척으로 — 다르면 누른 자리와 옮겨 가는 자리가 어긋난다
            el.scrollTop = minimapScrollTarget({ y: ev.clientY - rect.top, height: rect.height, lineCount, scrollHeight: el.scrollHeight, clientHeight: el.clientHeight });
        };
        doScroll(e);
        listenDrag(e, doScroll);   // 분리 창에서도 그 창의 문서로(utils/dragListen)
    }, [activeTab]);

    if (!activeTab) {
        return <div className={`${styles.subTabsContent} ${styles.executionPlanContent}`}>
            <div className={styles.noTabsMessage}>{t('common:mdt.noPlanTab')}</div>
        </div>;
    }

    const lines = activeTab.data.split('\n');
    const nodeLineMap = new Map();
    if (parsed) {
        for (const node of parsed.nodes) nodeLineMap.set(node.lineIndex, node);
    }

    // 활성 노드의 Predicate 정보
    const activePreds = (activePlanNodeId != null && parsed?.predicates[activePlanNodeId]) || [];

    return (
        <div className={`${styles.subTabsContent} ${styles.planSplitContainer}`}>
            {/* 좌측: 실행계획 + Predicate + 미니맵 */}
            <div className={styles.planLeftPane} style={{ position: 'relative' }}>
              <div className={styles.planToolRow}>
                <div className="editor-search-bar" style={{ margin: '4px 4px 2px 4px', flexShrink: 0, width: 'fit-content', maxWidth: '320px' }}>
                    <span style={{ display: 'flex', alignItems: 'center' }}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></span>
                    <input
                        className="editor-search-input"
                        type="text"
                        value={planSearch.text}
                        onChange={(e) => handlePlanSearch(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handlePlanSearchNav(e.shiftKey ? 'prev' : 'next'); if (e.key === 'Escape') setPlanSearch(p => ({ ...p, text: '' })); }}
                        placeholder={t('common:mdt.searchPlan')}
                    />
                    {planSearch.text && <span className="editor-search-count">{planSearch.matchCount > 0 ? `${planSearch.matchIndex + 1}/${planSearch.matchCount}` : '0'}</span>}
                    <button className="editor-search-nav" onClick={() => handlePlanSearchNav('prev')} title={t('common:mdt.prev')}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="18 15 12 9 6 15"/></svg></button>
                    <button className="editor-search-nav" onClick={() => handlePlanSearchNav('next')} title={t('common:mdt.next')}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg></button>
                    {planSearch.text && <button className="editor-search-nav" onClick={() => setPlanSearch(p => ({ ...p, text: '', matchIndex: -1, matchCount: 0, matchLines: [] }))} title={t('common:mdt.clear')}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>}
                </div>
                {/* 실행계획 옵션·다시 분석·AI 튜닝(2026-10-26 — SQL 실행계획 분석 메뉴를 여기로 합쳤다) */}
                {onPlanOptionsChange && (
                  <div className={styles.planToolBtns}>
                    <PlanOptionsMenu options={planOptions} onChange={onPlanOptionsChange} />
                    {onRerunPlan && activeTab && activeTab.query && (
                      <button type="button" className={styles.planToolBtn} onClick={() => onRerunPlan(activeTab)} title={t('common:plo.rerunHint')}>
                        <FiRefreshCw size={12} /> {t('common:plo.rerun')}
                      </button>
                    )}
                    {onAiTunePlan && activeTab && activeTab.query && (
                      <button type="button" className={styles.planToolBtn} onClick={() => onAiTunePlan(activeTab)} title={t('common:tun.autoTuneHint')}>
                        <FiCpu size={12} /> {t('common:plo.aiTune')}
                      </button>
                    )}
                  </div>
                )}
                {/* 이 계획 **하나에만** 작용하는 것들이라 메뉴가 아니라 대상 가까이 둔다
                    (docs/ui-command-placement.md 규칙 4 — 결과 CSV 저장과 같은 자리다). */}
                {hasPlanText && (
                  <div className={styles.planToolBtns}>
                    <button type="button" className={styles.planToolBtn} onClick={handleCopyPlan} title={t('common:plan.copyHint')}>
                      <FiCopy size={12} /> {copied ? t('common:plan.copied') : t('common:plan.copy')}
                    </button>
                    {onExportPlanFile && (
                      <>
                        <button type="button" className={styles.planToolBtn} onClick={() => onExportPlanFile(activeTab, 'md')} title={t('common:plan.exportMdHint')}>
                          <FiDownload size={12} /> .md
                        </button>
                        <button type="button" className={styles.planToolBtn} onClick={() => onExportPlanFile(activeTab, 'json')} title={t('common:plan.exportJsonHint')}>
                          <FiDownload size={12} /> .livesqlplan
                        </button>
                      </>
                    )}
                    {onSavePlanToArchive && (
                      <button type="button" className={styles.planToolBtn} onClick={() => onSavePlanToArchive(activeTab)} title={t('common:plan.saveToArchiveHint')}>
                        <FiBookmark size={12} /> {t('common:plan.saveToArchive')}
                      </button>
                    )}
                    {/* 이 계획으로 SQL 진단 리포트 만들기 — DB 연결 없이 진단·개선안(추정)·리포트(docs/features/22-sql-review.md) */}
                    {onPlanToReview && (
                      <button type="button" className={styles.planToolBtn} onClick={() => onPlanToReview(activeTab)} title={t('common:srv.fromPlanHint')}>
                        <FiFileText size={12} /> {t('common:srv.fromPlan')}
                      </button>
                    )}
                    {/* SQL Monitor 리포트 — 받아 둔 것이 있으면 열고, 없으면 묻고 나서 실제 + 리포트로 다시 뜬다(App.handlePlanMonitorReport, 2026-10-29) */}
                    {onPlanMonitorReport && activeTab && activeTab.query && (
                      <button
                        type="button" className={styles.planToolBtn} onClick={() => onPlanMonitorReport(activeTab)}
                        title={activeTab.monitorReport ? t('common:plan.monitorBtnHintHave') : t('common:plan.monitorBtnHint')}
                      >
                        <FiActivity size={12} /> {t('common:plan.monitorBtn')}
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className={styles.planScrollWrap}>
                <SplitView isSplit={isResultSplit}>
                <div ref={planScrollRef} className={styles.planScrollArea}>
                    <pre className={styles.planTextOutput}>
                        {lines.map((line, idx) => {
                            const node = nodeLineMap.get(idx);
                            const isBookmarked = bookmarkedLines.has(idx);
                            const lineNumClass = `${styles.planLineNum} ${isBookmarked ? styles.planLineBookmarked : ''}`;

                            if (node) {
                                const isActive = activePlanNodeId === node.id;
                                return (
                                    <span key={idx} data-line={idx} className={`${styles.planRow} ${isActive ? styles.planRowActive : ''} ${node.hasAsterisk ? styles.planRowPredicate : ''} ${isBookmarked ? styles.planLineBookmarkedBg : ''} ${planSearch.matchLines.includes(idx) ? styles.planSearchMatch : ''}`}>
                                        <span className={lineNumClass} onClick={(e) => { e.stopPropagation(); togglePlanBookmark(idx); }}>{String(idx + 1).padStart(3)}</span>
                                        <span onClick={() => {
                                            if (onPlanNodeClick) onPlanNodeClick(node, activeTab);
                                            if (node.objectName) {
                                                // 상세 칸이 접혀 있으면 편다 — 접힌 채로는 눌러도 아무것도 안 보여 「클릭이 안 된다」로 읽혔다
                                                if (!showPlanObjectDetail && onRequestObjectDetail) onRequestObjectDetail();
                                                scrollToObject(node.objectName.toUpperCase());
                                            }
                                        }}
                                            title={parsed.predicates[node.id] ? parsed.predicates[node.id].map(p => `${p.type}: ${p.condition}`).join('\n') : (node.objectName ? `Object: ${node.objectName}` : node.operation)}
                                        >{monoWide(line, styles.wideCh)}</span>{'\n'}
                                    </span>
                                );
                            }
                            const predLineMatch = line.match(/^\s*(\d+)\s*-\s*(access|filter)/);
                            const isPredHL = predLineMatch && parseInt(predLineMatch[1]) === activePlanNodeId;
                            return (
                                <span key={idx} data-line={idx} className={`${isPredHL ? styles.planRowPredicateHighlight : ''} ${isBookmarked ? styles.planLineBookmarkedBg : ''} ${planSearch.matchLines.includes(idx) ? styles.planSearchMatch : ''}`}>
                                    <span className={lineNumClass} onClick={(e) => { e.stopPropagation(); togglePlanBookmark(idx); }}>{String(idx + 1).padStart(3)}</span>
                                    {monoWide(line, styles.wideCh)}{'\n'}
                                </span>
                            );
                        })}
                    </pre>
                </div>
                </SplitView>
                <canvas ref={planMinimapRef} className={styles.planMinimap} onMouseDown={handlePlanMinimapDrag} title={t('common:mdt.minimap')} />
              </div>
                {/* SQL Monitor 리포트 — 받아 온 경우에만, 접어서(2026-10-07). 보관함에 저장하면 함께 남는다 */}
                {activeTab && activeTab.monitorReport && (
                    <details className={styles.monitorReport}>
                        <summary>{t('common:plan.monitorReport')}</summary>
                        <pre>{activeTab.monitorReport}</pre>
                    </details>
                )}
                {/* 활성 노드 Predicate 요약 (하단) */}
                {activePreds.length > 0 && (
                    <div className={styles.predSummary}>
                        <div className={styles.predSummaryTitle}>Predicate (ID: {activePlanNodeId})</div>
                        {activePreds.map((p, i) => (
                            <div key={i} className={`${styles.predItem} ${styles[`pred_${p.type}`]}`}>
                                <span className={styles.predType}>{p.type}</span>
                                <code>{p.condition}</code>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* 우측: 오브젝트 상세 — **기본은 접힘**이고 결과 툴바의 토글로 편다
                (사용자 요청 2026-09-20). 늘 펼쳐져 있어 계획 본문이 그만큼 좁았다. */}
            {showPlanObjectDetail && (
            <div className={`${styles.planRightPane} ${!isResultSplit ? styles.planRightPaneWide : ''}`} ref={rightPanelRef}>
                <div className={styles.planRightHeader}>
                    <div className={styles.planRightTitle}>{t('common:mdt.objectDetail')}</div>
                    <div className="editor-search-bar" style={{ margin: '4px 8px 6px 8px' }}>
                        <span style={{ display: 'flex', alignItems: 'center' }}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></span>
                        <input
                            className="editor-search-input"
                            type="text"
                            value={selectedObject || ''}
                            // **적는 글을 바꿔 넣지 않는다.** 대문자로 바꿔 넣으면 리액트가 값을 다시 쓰면서 커서가 끝으로 가고(가운데를 고칠 수 없다)
                            // 한글 조합도 깨진다(신고 2026-10-13). 비교할 때만 대문자로 본다(아래 matched) — 보이는 것은 CSS 로 대문자
                            onChange={(e) => setSelectedObject(e.target.value || null)}
                            onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) lookupTyped(); }}
                            placeholder={t('common:mdt.searchTableIndex')}
                            style={{ width: '100%', textTransform: 'uppercase' }}
                            title={t('common:mdt.searchEnterHint')}
                        />
                        {selectedObject && (
                            <button className="editor-search-nav" onClick={() => setSelectedObject(null)} title={t('common:mdt.clearFilter')}>
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                        )}
                    </div>
                </div>
                <div className={styles.planRightBody}>
                {/* 증상·비슷한 사례·운영 실행 이력. 보여 줄 게 없으면 스스로 안 그린다. */}
                <PlanInsights
                    planText={typeof activeTab?.data === 'string' ? activeTab.data : null}
                    connectionId={statsConnectionId}
                    sqlText={activeTab?.query}
                />
                {!selectedObject && (
                    <div className={styles.planRightEmpty}>{t('common:mdt.planPickHint')}</div>
                )}
                {/* 조회 결과에서 뽑은 리본 — 유형과 스키마. 누르면 그 필터가 켜지고 다시 누르면 꺼진다.
                    아무것도 안 켜져 있으면 **미적용**(전부 보임)이다. */}
                {selectedObject && matched.tables.length > 0 && (
                    <div className={styles.objRibbon}>
                        <button
                            type="button"
                            className={`${styles.objChip} ${typeFilter.has('table') ? styles.objChipOn : ''}`}
                            onClick={() => toggleType('table')}
                            title={t('common:mdt.ribbonToggle')}
                        >
                            {t('common:mdt.ribbonTables')} <span className={styles.objChipCount}>{matched.tables.length}</span>
                        </button>
                        {matched.indexCount > 0 && (
                            <button
                                type="button"
                                className={`${styles.objChip} ${typeFilter.has('index') ? styles.objChipOn : ''}`}
                                onClick={() => toggleType('index')}
                                title={t('common:mdt.ribbonToggle')}
                            >
                                {t('common:mdt.ribbonIndexes')} <span className={styles.objChipCount}>{matched.indexCount}</span>
                            </button>
                        )}
                        {/* 스키마가 하나뿐이면 고를 것이 없다 — 칩을 만들지 않는다 */}
                        {matched.schemas.length > 1 && <span className={styles.objRibbonSep} />}
                        {matched.schemas.length > 1 && matched.schemas.map(sc => (
                            <button
                                key={sc.owner}
                                type="button"
                                className={`${styles.objChip} ${schemaFilter.has(sc.owner) ? styles.objChipOn : ''}`}
                                onClick={() => toggleSchema(sc.owner)}
                                title={t('common:mdt.ribbonToggle')}
                            >
                                {sc.owner} <span className={styles.objChipCount}>{sc.n}</span>
                            </button>
                        ))}
                        {/* 인덱스 통계는 **필터가 아니라 표시 옵션**이라 리본과 갈라 오른쪽에 둔다 */}
                        {showIndexes && matched.indexCount > 0 && (
                            <label className={styles.objDetailToggle} title={t('common:mdt.indexDetailHint')}>
                                <input type="checkbox" checked={showIdxStats} onChange={(e) => setShowIdxStats(e.target.checked)} />
                                {t('common:mdt.indexDetail')}
                            </label>
                        )}
                    </div>
                )}
                {/* 받아 둔 것 안에 없다 — Enter 로 DB 에서 찾을 수 있다고 말한다(타이핑은 이 계획의 객체 안에서만 거른다) */}
                {selectedObject && matched.tables.length === 0 && (
                    <div className={styles.planRightEmpty}>{lookupBusy ? t('common:state.loading') : t('common:mdt.searchNotLoaded')}</div>
                )}
                {selectedObject && matched.tables.length > 0 && visibleTables.length === 0 && (
                    <div className={styles.planRightEmpty}>{t('common:mdt.ribbonNoMatch')}</div>
                )}
                {visibleTables.map((tbl, i) => {
                    const name = tbl.TABLE_NAME;
                    const isHL = highlightObject === name;
                    return (
                        <div key={i} data-obj={name} className={`${styles.objCard} ${isHL ? styles.objCardHighlight : ''}`}>
                            <div className={styles.objCardHeader}>
                                <span className={styles.objCardIcon}>T</span>
                                <span className={styles.objCardSchema}>{tbl.OWNER}.</span><span className={styles.objCardName}>{name}</span>
                                {/* 주석은 이름 **오른편**에 붙인다. 아래 줄에 따로 두면 카드가 그만큼 길어져
                                    한 화면에 보이는 오브젝트 수가 줄었다(신고 2026-09-18). */}
                                {tbl.TABLE_COMMENT && showTables && (
                                    <span className={styles.objCardComment} title={tbl.TABLE_COMMENT}>{tbl.TABLE_COMMENT}</span>
                                )}
                                {/* 파티션 여부는 기본 정보다 — 접근 방식과 통계 해석이 통째로 달라진다 */}
                                {tbl.PARTITIONED === 'YES' && (
                                    // 종류와 키까지 — 「파티션」 한 마디로는 접근 방식을 가늠할 수 없다
                                    <span
                                        className={styles.partBadge}
                                        title={tbl.PARTITION_KEY ? t('common:mdt.partitionKeyHint', { key: tbl.PARTITION_KEY }) : undefined}
                                    >
                                        {tbl.PARTITIONING_TYPE
                                            ? `${tbl.PARTITIONING_TYPE}${tbl.SUBPARTITIONING_TYPE && tbl.SUBPARTITIONING_TYPE !== 'NONE' ? `/${tbl.SUBPARTITIONING_TYPE}` : ''}`
                                              + `${tbl.PARTITION_KEY ? ` (${tbl.PARTITION_KEY})` : ''}`
                                              + `${tbl.PARTITION_COUNT ? ` · ${tbl.PARTITION_COUNT}` : ''}`
                                            : t('common:mdt.partitioned')}
                                    </span>
                                )}
                                {/* 전체 DDL — 컬럼·제약·인덱스를 한 번에 본다 */}
                                {onShowTableDDL && (
                                    <button
                                        type="button"
                                        className={styles.ddlBtn}
                                        onClick={(e) => { e.stopPropagation(); onShowTableDDL(tbl.OWNER, name); }}
                                        title={t('common:mdt.showDdlHint')}
                                    >
                                        {t('common:mdt.showDdl')}
                                    </button>
                                )}
                            </div>
                            {showTables && <div className={styles.objStatGrid}>
                                <div className={styles.objStatItem}><span className={styles.objStatLabel}>Rows</span><span className={styles.objStatValue}>{tbl.NUM_ROWS?.toLocaleString() ?? '-'}</span></div>
                                <div className={styles.objStatItem}><span className={styles.objStatLabel}>Blocks</span><span className={styles.objStatValue}>{tbl.BLOCKS?.toLocaleString() ?? '-'}</span></div>
                                <div className={styles.objStatItem}><span className={styles.objStatLabel}>Avg Row Len</span><span className={styles.objStatValue}>{tbl.AVG_ROW_LEN ?? '-'}</span></div>
                                <div className={styles.objStatItem}><span className={styles.objStatLabel}>Analyzed</span><span className={styles.objStatValue}>{tbl.LAST_ANALYZED ? new Date(tbl.LAST_ANALYZED).toLocaleDateString() : '-'}</span></div>
                            </div>}
                            {/* 이 테이블의 인덱스 */}
                            {/* **소유자까지 본다.** 이름만 비교하면 같은 테이블명이 여러 스키마에 있을 때
                                (서버는 소유자를 못 좁히면 모든 소유자 행을 그대로 준다) 각 카드가
                                양쪽 인덱스를 전부 그려 **같은 인덱스가 두 번** 나온다(신고 2026-09-18). */}
                            {showIndexes && (() => {
                                const mine = objectStats.indexes.filter(idx => idx.TABLE_NAME === name && idx.TABLE_OWNER === tbl.OWNER);
                                if (!mine.length) return null;
                                const cardKey = `${tbl.OWNER}.${name}`;
                                const q = String(selectedObject || '').toUpperCase();
                                // 찾은 것이 이 테이블의 인덱스면 처음부터 펼친다 — 접힌 채면 누른 인덱스가 어디 있는지 안 보인다
                                const hitIndex = !!q && !name.includes(q) && mine.some(idx => idx.INDEX_NAME.includes(q));
                                const shown = openIdx.has(`!${cardKey}`) ? false : (openIdx.has(cardKey) || hitIndex);
                                return (
                                    <>
                                        <button type="button" className={styles.idxToggle} aria-expanded={shown}
                                            onClick={() => setOpenIdx((prev) => {
                                                const next = new Set(prev);
                                                // 펼침은 키로, 「자동으로 펼친 것을 접음」 은 !키로 기억한다
                                                if (shown) { next.delete(cardKey); next.add(`!${cardKey}`); } else { next.delete(`!${cardKey}`); next.add(cardKey); }
                                                return next;
                                            })}>
                                            <span className={styles.idxToggleArrow}>{shown ? '▾' : '▸'}</span>
                                            {t('common:mdt.indexCount', { count: mine.length })}
                                        </button>
                                        {shown && mine.map((idx, j) => {
                                const idxHL = highlightObject === idx.INDEX_NAME;
                                return (
                                    <div key={idx.INDEX_NAME || j} data-obj={idx.INDEX_NAME} className={`${styles.idxCard} ${idxHL ? styles.objCardHighlight : ''}`}>
                                        <div className={styles.idxHeader}>
                                            <span className={styles.idxIcon}>{idx.UNIQUENESS === 'UNIQUE' ? 'U' : 'I'}</span>
                                            <span className={styles.idxName}>{idx.INDEX_NAME}</span>
                                            {/* GLOBAL/LOCAL 만 적으면 무엇의 구분인지 모른다 — 「파티션」을 붙인다.
                                                인라인 스타일이던 것을 클래스로 뺐다(다크에서 밝은 상자로 남았다). */}
                                            {idx.INDEX_TYPE && idx.INDEX_TYPE !== 'NONE' && (
                                                // NONPART = 파티션 테이블의 비파티션(GLOBAL) 인덱스 — 「파티션 LOCAL」 로 잘못 보이던 것(2026-10-27)
                                                <span className={styles.partBadge} title={idx.INDEX_TYPE === 'NONPART' ? t('common:mdt.nonPartIndexHint') : undefined}>
                                                    {idx.INDEX_TYPE === 'NONPART' ? t('common:mdt.nonPartIndex') : `${t('common:mdt.partitioned')} ${idx.INDEX_TYPE}`}
                                                </span>
                                            )}
                                            <span className={styles.idxStatus}>{idx.STATUS}</span>
                                        </div>
                                        <div className={styles.idxCols}>{idx.COLUMNS}</div>
                                        {/* 기본으로 보이는 것은 컬럼 목록과 생성일시뿐이다 */}
                                        <div className={styles.idxCreated}>
                                            {t('common:mdt.created')} {idx.IDX_CREATED ? new Date(idx.IDX_CREATED).toLocaleString() : '-'}
                                        </div>
                                        {showIdxStats && <div className={styles.objStatGrid}>
                                            <div className={styles.objStatItem}><span className={styles.objStatLabel}>Rows</span><span className={styles.objStatValue}>{idx.IDX_NUM_ROWS?.toLocaleString() ?? '-'}</span></div>
                                            <div className={styles.objStatItem}><span className={styles.objStatLabel}>Leaf Blocks</span><span className={styles.objStatValue}>{idx.LEAF_BLOCKS ?? '-'}</span></div>
                                            <div className={styles.objStatItem}><span className={styles.objStatLabel}>B-Level</span><span className={styles.objStatValue}>{idx.BLEVEL ?? '-'}</span></div>
                                            <div className={styles.objStatItem}><span className={styles.objStatLabel}>Analyzed</span><span className={styles.objStatValue}>{idx.IDX_LAST_ANALYZED ? new Date(idx.IDX_LAST_ANALYZED).toLocaleDateString() : '-'}</span></div>
                                        </div>}
                                    </div>
                                );
                                        })}
                                    </>
                                );
                            })()}
                        </div>
                    );
                })}
                </div>{/* planRightBody */}
            </div>
            )}
        </div>
    );
};

export default InteractivePlanContent;
